import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Separator } from "../ui/separator";

const TermsAndConditions = (): JSX.Element => {
  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <Card>
        <CardHeader className="space-y-2">
          <CardTitle className="text-2xl">Terms of Service</CardTitle>
          <CardDescription className="text-sm">
            Effective Date: 01/01/2025<br />
            Last Updated: 01/10/2025<br />
            Version: 1.4.0
          </CardDescription>
        </CardHeader>
        <CardContent className="prose max-w-none space-y-8">
          <div className="bg-muted/50 p-6 rounded-lg">
            <p className="text-lg leading-relaxed">
              Welcome to Upper Echelon, a platform owned and operated by UENETWORK LTD ("Company", "we", "us", or "our"). 
              By accessing or using our website, you ("User", "you", or "your") agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, please do not use our website.
            </p>
          </div>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">1.</span> Eligibility
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                You must be at least 18 years of age to use Upper Echelon.
              </p>
              <p>
                By using our website, you represent and warrant that you meet this age requirement.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">2.</span> Use of Services
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                Upper Echelon provides tools and services related to assisting with OnlyFans profile management, including but not limited to, 
                sales, marketing, customer interaction, and account management.
              </p>
              <p>
                Users agree to use Upper Echelon solely for its intended purpose and in compliance with all applicable laws and regulations.
              </p>
              <p>
                We reserve the right to refuse service to anyone for any reason at any time.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">3.</span> User Responsibilities
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                You agree not to engage in any activity that may interfere with or disrupt the website, including but not limited to 
                hacking, data scraping, or transmitting malicious code.
              </p>
              <p>
                You shall not use Upper Echelon for any unlawful, fraudulent, or unethical activities.
              </p>
              <p>
                You are responsible for maintaining the confidentiality of any login credentials and for all activities conducted under your account.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">4.</span> Confidentiality and Non-Disclosure
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                You acknowledge that any confidential information obtained through the use of our platform must remain confidential.
              </p>
              <p>
                You agree not to share or disclose any sensitive information related to the Company, its clients, or OnlyFans account activities.
              </p>
              <p>
                If you gain access to data, features, or information that you believe you should not have access to (including but not limited to elevated permissions, restricted user data, or administrative functions), you must immediately report this to the Company and refrain from accessing, using, modifying, or sharing such information. Failure to report unauthorized access or any abuse of such access will result in immediate termination and may lead to legal action.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">5.</span> Intellectual Property
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                All content and materials provided on Upper Echelon, including text, images, and software, are owned by UENETWORK LTD or its licensors.
              </p>
              <p>
                Users may not copy, reproduce, modify, or distribute any content from our website without express written permission.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">6.</span> Payment and Compensation
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                Compensation for services performed through Upper Echelon is subject to agreements between Users and UENETWORK LTD.
              </p>
              <p>
                By providing your bank details, you:
                (a) Confirm that the information is accurate and you are authorized to use the account
                (b) Consent to us storing and processing this information for payment purposes
                (c) Understand that we use secure third-party payment processors to handle transactions
              </p>
              <p>
                We reserve the right to modify payment structures and terms at any time with prior notice to affected Users.
              </p>
              <p>
                There is no guarantee of specific or minimum earnings through the use of our services.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">7.</span> Limitation of Liability
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                Upper Echelon is provided "as is" without warranties of any kind, either express or implied.
              </p>
              <p>
                The Company is not liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of the website.
              </p>
              <p>
                While we implement industry-standard security measures to protect your financial information, you acknowledge that no method of electronic storage or transmission is 100% secure. You agree that we are not liable for unauthorized access to your bank details unless caused by our gross negligence.
              </p>
              <p>
                Users agree to indemnify and hold harmless UENETWORK LTD from any claims, losses, or damages arising from their use of the platform.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">8.</span> Termination
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                We reserve the right to terminate or suspend access to Upper Echelon at our discretion, with or without notice.
              </p>
              <p>
                Termination does not entitle the User to any form of severance or further compensation beyond what is contractually agreed.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">9.</span> Dispute Resolution & Governing Law
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                These Terms are governed by the laws of England and Wales.
              </p>
              <p>
                Any disputes shall be resolved through informal negotiation first. If a resolution is not reached, mediation or alternative 
                dispute resolution may be considered before pursuing legal action.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="text-brand-gold">10.</span> Modifications to Terms
            </h2>
            <Separator className="my-2" />
            <div className="pl-6 space-y-4">
              <p>
                We reserve the right to update or modify these Terms at any time. Users will be notified of any material changes, and 
                continued use of the website constitutes acceptance of the revised Terms.
              </p>
            </div>
          </section>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsAndConditions; 
